import React, { useState, useEffect, useCallback, memo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Typography,
  Input,
  InputAdornment,
  IconButton,
  ButtonGroup
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { makeStyles } from "@mui/styles";
import { Typography as FTypography, Stack } from "@financeable-com-au/financeable-ui";
import moment from "moment";
import { startCase, debounce, upperFirst } from "lodash";
import clsx from "clsx";
import { openSnackbar } from "@/store/slices/snackbarSlice";
import { ERRORS_MESSAGE, STATUS_CODE, SUCCESS_MESSAGE, userType, userTypeOptions } from "../../constants";
import { getAllUsers, resendUserInvitation, saveValue, updateUserRole, userSelector } from "../../store/slices/userSlice";
import DataGridTable from "../dataGridTable/DataGridTable";

export const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  borderRemove: {
    "& fieldset": {
      border: "0px !important",
    },
  },
  margin: {
    margin: theme.spacing(1),
  },
  withoutLabel: {
    marginTop: theme.spacing(3),
  },
  textField: {
    width: "61ch",
  },
}));


const UserList = memo(() => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const pageSizeRef = useRef(10);
  const userStatusFilterRef = useRef("Active");
  const user = useSelector(userSelector);
  const { permissions, users, gettingUsers, page, isResendUserEmail } = user
  const [rows, setRows] = useState([]); 
  const [searchTerm, setSearchTerm] = useState("");
  const [rowCountState, setRowCountState] = useState(users?.totalDocs || 0);
  const [params, setParams] = useState({ offset: 1, limit: 10 });
  const usersFilters = ["Active", "Invited"];
  const isResendUserEmailLoading = isResendUserEmail?.isSendingEmail;

  const handle = {
    getAllUsersFn: ({ cursor = 1, search, filter = userStatusFilterRef.current || "Active" }) => {
      dispatch(getAllUsers({ cursor, limit: pageSizeRef?.current || 10, search, filter }));
    },
    handleUserSearch: (searchTerm) => {
      (searchTerm.length >= 2 || searchTerm.length <= 0) && handle.getAllUsersFn({ cursor: users?.page, search: searchTerm });
    },
    handleFilterChange: async (option) => {
      handle.getAllUsersFn({
        cursor: 1,
        search: searchTerm,
        filter: option,
      });
      userStatusFilterRef.current = option;
    },
    resendUserInvitation: (email) => {
      dispatch(resendUserInvitation(email)).then((res) => {
        if (res?.error) {
          dispatch(openSnackbar({
            message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
            intent: "danger",
          }));
        } else {
          dispatch(openSnackbar({
            message: res?.payload?.data?.status_message || SUCCESS_MESSAGE.resendUserInvitation,
            intent: "success",
          }));
        }
      });
    }
  };

  const handleUserUpdate = (id, value) => {
    const data = {
      userId: id,
      data: {
        userType: value,
      },
    };
    dispatch(updateUserRole(data)).then(({ payload }) => {
      if (payload?.status_code === STATUS_CODE.success) {
        dispatch(openSnackbar({
          message: payload?.status_message,
          intent: "success",
        }));
        const updatedRow = rows?.map((doc) => (doc?.id === id ? { ...doc, role: value } : doc));
        setRows(updatedRow);
      } else {
        dispatch(openSnackbar({
          message: payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
          intent: "danger",
        }));
      }
    });
  };

  const activeUserColumns = [
    {
      field: "date",
      headerName: "Date",
      type: "string",
      width: 100,
      editable: false,
      renderCell: (params) => (
        <div>
          <Typography style={{ fontSize: "14px" }}>{params.value.date}</Typography>
          <Typography style={{ fontSize: "11px", color: "#999" }}>{params.value.time}</Typography>
        </div>
      ),
    },
    {
      field: "brokerOrganisation",
      headerName: "Broker",
      width: 230,
      // editable: true,
    },
    {
      field: "name",
      headerName: "Name",
      width: 180,
      // editable: true,
    },
    {
      field: "email",
      headerName: "Email",
      width: 280,
      // editable: true,
    },
    // {
    //   field: "status",
    //   headerName: "Status",
    //   width: 100,
    //   editable: permissions?.user?.status_change ? true : false,
    //   // renderCell: ({ row }) => <StatusCell rowInfo={row} rows={rows} setRows={setRows} />,
    // },
    {
      field: "roles",
      headerName: "User Roles",
      width: 150,
      // editable: true,
      renderCell: ({ row }) =>
        [userType.master].includes(row?.role) ? (
          <Typography sx={{ textAlign: "center", margin: 2 }}>{startCase(row?.role)}</Typography>
        ) : (
          <FormControl fullWidth>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={row?.role || ""}
              disabled={!permissions?.user?.edit_role}
              className={classes.borderRemove}
              onChange={(event) => handleUserUpdate(row?.id, event.target.value)}
            >
              {userTypeOptions
                .filter((el) => el !== userType.master)
                .map((item) => (
                  <MenuItem
                    value={item}
                    key={item}
                    disabled={item === userType.master} // owner
                  >
                    {startCase(item)}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        ),
    },
    {
      field: "referrer",
      headerName: "Referrer",
      sortable: false,
      width: 120,
      renderCell: (params) => {
        const isInternalSubmit = !!params?.row?.usersPermission?.application?.internal_submit;
        return (
          <Stack width="100%" style={{ marginLeft: "20px" }}>
            <FTypography level="body-sm">{isInternalSubmit ? "Yes" : "No"}</FTypography>
          </Stack>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      sortable: false,
      width: 210,
      renderCell: (params) => (
        <Button onClick={() => navigate(`/user/rolepermissions/${params?.id}`)} variant="outlined">
          edit Permission
        </Button>
      ),
    },
  ];

  const invitedUserColumns = [
    {
      field: "date",
      headerName: "Date",
      type: "string",
      width: 100,
    },
    {
      field: "brokerOrganisation",
      headerName: "Broker",
      width: 230,
    },
    {
      field: "email",
      headerName: "Email",
      width: 280,
    },
    {
      field: "action",
      headerName: "Action",
      sortable: false,
      width: 210,
      renderCell: (params) => (
        <Button
          disabled={isResendUserEmailLoading && isResendUserEmail?.resendEmail === params?.row?.email}
          onClick={() => handle.resendUserInvitation(params?.row?.email)}
          variant="outlined"
        >
          {isResendUserEmailLoading && isResendUserEmail?.resendEmail === params?.row?.email ? "Sending..." : "Resend Invitation"}
        </Button>
      ),
    },
  ];

  const tableColumns = userStatusFilterRef.current === "Active" ? activeUserColumns : invitedUserColumns;

  useEffect(() => {
    setRowCountState((prevRowCountState) => users?.totalDocs ?? prevRowCountState);
  }, [users?.totalDocs]);

  useEffect(() => {
    if (permissions?.user && permissions?.user?.access !== "all" && permissions?.user?.access !== "own") {
      navigate("/404");
    }
  }, [permissions]);

  useEffect(() => {
    if (Array.isArray(users?.docs) && users.docs.length > 0) {
      const usersRows = users.docs.map((doc) => {
        const date = new Date(doc?.createdAt);
        return {
          id: doc?._id,
          name: (doc?.firstName || "") + " " + (doc?.lastName || ""),
          brokerOrganisation: doc?.organisation?.name,
          email: doc?.email?.address || doc?.email, // invited user has email value directly
          userId: doc?._id,
          cursor: users?.page,
          status: upperFirst(doc?.status),
          role: doc?.userType,
          date: {
            date: moment(date).format("D-M-YY"),
            time: moment(date).format("h:mm a"),
          },
          usersPermission: doc?.usersPermission,
        };
      })
      setRows(usersRows)
    }
  }, [users]);

  useEffect(() => {
    if (params?.offset) {
      handle.getAllUsersFn({
        cursor: params?.offset,
        search: searchTerm,
        filter: userStatusFilterRef.current,
      });
      dispatch(saveValue({ page: params?.offset, limit: params?.limit }));
    }
  }, [params?.offset, params?.sort, params?.limit]);

  const debounced = useCallback(debounce(handle.handleUserSearch, 500), []);

  return (
    <Grid container item direction="column" spacing={0} style={{ padding: `20px` }}>
      <Grid
        item
        style={{
          borderBottom: "1px solid rgba(224, 224, 224, 1)",
          display: "flex",
          alignItems: "center",
          columnGap: "50px",
        }}
      >
        <Typography
          variant="h1"
          style={{
            fontSize: "24px",
            fontWeight: 800,
            letterSpacing: "-0.5px",
          }}
        >
          Users
        </Typography>
        <div className={classes.root}>
          <FormControl className={clsx(classes.margin, classes.textField)}>
            <Input
              className="input-border-bottom"
              id="user-search"
              autoComplete="search" // false
              variant="filled"
              placeholder="Search…"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                debounced(e.target.value, 1000);
              }}
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              }
              endAdornment={
                searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        setSearchTerm("");
                        searchTerm !== "" && debounced("", 1000);
                      }}
                      color="secondary"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }
            />
          </FormControl>
        </div>
        <ButtonGroup variant="outlined">
          {usersFilters.map((option) => (
            <Button
              key={option}
              size="small"
              onClick={() => {
                handle.handleFilterChange(option);
              }}
              variant={userStatusFilterRef.current === option ? "contained" : "outlined"}
            >
              {option}
            </Button>
          ))}
        </ButtonGroup>
      </Grid>

      <Grid item>
        <DataGridTable
          onCellClick={() => { }}
          onRowClick={() => { }}
          data={rows || []}
          columns={tableColumns}
          rowsPerPageOptions={[10, 20, 50, 100]}
          pageSize={pageSizeRef?.current || 10}
          rowCount={rowCountState}
          params={params}
          setParams={setParams}
          page={page}
          isLoading={gettingUsers}
          pageSizeRef={pageSizeRef}
          columnVisibilityModel={{
            action: !!permissions?.user?.permission_edit,
          }}
        />
      </Grid>
    </Grid>
  );
});

export default UserList;
